import React, { useCallback, useEffect, useRef, useState } from "react";
import { getUserSuggestions } from "../api/usersApi";

/**
 * Text input with dropdown of WillPlay usernames (search as you type; focus shows first page).
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {string} [props.id]
 */
function FriendSuggestInput({ value, onChange, placeholder, disabled, id }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const blurTimer = useRef(null);

  const load = useCallback(
    async (q) => {
      setLoading(true);
      try {
        const { response, data } = await getUserSuggestions({
          q: q.trim() || undefined,
          take: 50
        });
        if (!response.ok) {
          setItems([]);
          return;
        }
        const raw = Array.isArray(data) ? data : [];
        const mapped = raw.map((row) => ({
          userId: row.userId ?? row.UserId,
          username: (row.username ?? row.Username ?? "").toString()
        }));
        setItems(mapped.filter((x) => x.username));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      void load(value);
    }, 220);
    return () => clearTimeout(t);
  }, [open, value, load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (username) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onChange(username);
    setOpen(false);
  };

  return (
    <div className="friend-suggest" ref={wrapRef}>
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        maxLength={120}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 180);
        }}
      />
      {open && !disabled ? (
        <ul className="friend-suggest__list" role="listbox">
          {loading ? (
            <li className="friend-suggest__hint muted">Loading…</li>
          ) : items.length === 0 ? (
            <li className="friend-suggest__hint muted">No users match. Try another name.</li>
          ) : (
            items.map((row) => (
              <li key={row.userId}>
                <button
                  type="button"
                  className="friend-suggest__option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(row.username)}
                >
                  {row.username}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export default FriendSuggestInput;
